// Ambient audio toggle. Off by default; user must explicitly enable (mobile autoplay rules).
//
// Two modes:
//   1) Project default — falls back to /assets/audio/ambient.mp3 when no room
//      override is set. Single click toggles play/pause.
//   2) Per-room override — App calls setRoomAmbient(url) on every room change.
//      If audio is currently enabled, crossfade to the new URL over 800ms via
//      a second HTMLAudioElement. If audio is disabled, the URL is queued for
//      the next toggle. If the new URL 404s, the swap aborts and the previous
//      track keeps playing — the button does NOT go into the global 🚫 state
//      just because one room's file is missing (only the initial-load failure
//      should disable the button).

import { getAnalytics } from '../playcanvas/analytics';

const DEFAULT_AMBIENT_URL = '/assets/audio/ambient.mp3';
const TARGET_VOLUME = 0.28; // bossa nova → background mood, not foreground
const CROSSFADE_MS = 800;

export class AudioControls {
  private readonly button: HTMLButtonElement;
  private readonly icon: HTMLElement;
  private audio: HTMLAudioElement | null = null;
  private currentUrl: string = DEFAULT_AMBIENT_URL;
  private enabled = false;
  private failed = false;
  private crossfadeRaf: number | null = null;

  constructor() {
    const btn = document.getElementById('audio-button') as HTMLButtonElement | null;
    if (!btn) throw new Error('[audio] #audio-button missing');
    this.button = btn;
    const icon = btn.querySelector('.icon-button__icon') as HTMLElement | null;
    if (!icon) throw new Error('[audio] icon span missing');
    this.icon = icon;
    this.button.addEventListener('click', () => this.toggle());
    this.render();
  }

  /**
   * Called by App on every room change with the current room's `ambientAudio`.
   * Pass `null` to fall back to the project default ambient loop.
   *
   *   - same URL as current → no-op
   *   - audio disabled → just update `currentUrl`, no playback yet
   *   - audio enabled → instantiate a new <audio>, crossfade 800ms,
   *                     swap, dispose the old one. On 404 from the new
   *                     track, abort the swap (leave old playing).
   */
  setRoomAmbient(url: string | null): void {
    const nextUrl = url ?? DEFAULT_AMBIENT_URL;
    if (nextUrl === this.currentUrl) return;
    if (this.failed) {
      // Global failure state — don't try anything; user already saw the 🚫.
      this.currentUrl = nextUrl;
      return;
    }

    this.currentUrl = nextUrl;

    if (!this.enabled || !this.audio) {
      // Not playing — next toggle will pick up the new URL.
      return;
    }

    // Audio is currently playing — crossfade.
    const oldAudio = this.audio;
    const newAudio = new Audio(nextUrl);
    newAudio.loop = true;
    newAudio.volume = 0;
    newAudio.preload = 'auto';

    let aborted = false;
    const abort = () => {
      if (aborted) return;
      aborted = true;
      newAudio.pause();
      // Keep oldAudio playing at TARGET_VOLUME — restore if mid-fade.
      this.cancelCrossfade();
      oldAudio.volume = TARGET_VOLUME;
    };
    newAudio.addEventListener('error', abort);

    void newAudio.play().then(() => {
      if (aborted) return;
      this.audio = newAudio;
      this.runCrossfade(oldAudio, newAudio, () => {
        // Cleanup: pause + release the old element so its decoder frees.
        oldAudio.pause();
        oldAudio.src = '';
      });
    }, abort);
  }

  private runCrossfade(from: HTMLAudioElement, to: HTMLAudioElement, onDone: () => void): void {
    this.cancelCrossfade();
    const startVolFrom = from.volume;
    const start = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - start) / CROSSFADE_MS);
      // Linear is fine for short fades; equal-power would matter for longer ones.
      from.volume = Math.max(0, startVolFrom * (1 - t));
      to.volume = Math.min(TARGET_VOLUME, TARGET_VOLUME * t);
      if (t < 1) {
        this.crossfadeRaf = requestAnimationFrame(step);
      } else {
        this.crossfadeRaf = null;
        onDone();
      }
    };
    this.crossfadeRaf = requestAnimationFrame(step);
  }

  private cancelCrossfade(): void {
    if (this.crossfadeRaf !== null) {
      cancelAnimationFrame(this.crossfadeRaf);
      this.crossfadeRaf = null;
    }
  }

  private toggle(): void {
    if (this.failed) return;
    if (!this.audio) {
      this.audio = new Audio(this.currentUrl);
      this.audio.loop = true;
      this.audio.volume = TARGET_VOLUME;
      this.audio.preload = 'auto';
      this.audio.addEventListener('error', () => {
        // First-load failure → assume the project default is missing → disable
        // the button entirely. Per-room file 404s after a successful first
        // load are handled in setRoomAmbient's abort path instead.
        this.failed = true;
        this.render();
        this.button.title = 'Áudio indisponível — adicione public/assets/audio/ambient.mp3';
      });
    }
    this.enabled = !this.enabled;
    if (this.enabled) {
      void this.audio.play().catch(() => {
        this.failed = true;
        this.render();
      });
    } else {
      this.audio.pause();
    }
    getAnalytics().log('audio_toggled', { enabled: this.enabled });
    this.render();
  }

  private render(): void {
    if (this.failed) {
      this.icon.textContent = '🚫';
      this.icon.dataset.state = 'failed';
      this.button.setAttribute('aria-label', 'Áudio indisponível');
      this.button.disabled = true;
      return;
    }
    this.icon.textContent = this.enabled ? '🔊' : '🔇';
    this.icon.dataset.state = this.enabled ? 'on' : 'muted';
    this.button.setAttribute('aria-label', this.enabled ? 'Silenciar áudio ambiente' : 'Ativar áudio ambiente');
  }
}
